import { mergeMap, catchError } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { FileModel, DeleteEntityModel, MoveFilesModel, FileDto } from '../../model/models';
import { IGetFilesByFolderRequestDetails } from '../../../file-manager';
import { FileManagerResult, FileManagerResults, FileParameter } from './filesList.service.model';
import { appendQueryParamToUrlString, customErrorMessage, isNotSuccessCode, parseSuccessDetails, tryAddingMoreContextToErrorMessage } from './filesList.utils';
import { ApiEndpointService } from '../../../services/api-endpoint.service';

@Injectable({ providedIn: 'root' })
export class FilesListApiService {
  constructor(private apiEndpoint: ApiEndpointService, private http: HttpClient) { }

  public getFiles = (data: IGetFilesByFolderRequestDetails): Observable<FileManagerResults> => {
    const url = this.apiEndpoint.GetFilesListUrl;
    const options = {
      body: JSON.stringify(data),
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'api-version': '2.0'
      })
    };
    return this.http.request<HttpResponse<Blob>>('post', url, options as unknown)
      .pipe(mergeMap(this.processGetFiles))
      .pipe(catchError(tryAddingMoreContextToErrorMessage(this.processGetFiles)));
  }

  private processGetFiles(response: HttpResponse<Blob>): Observable<FileManagerResults> {
    switch(response.status) {
      case 200: return parseSuccessDetails<FileManagerResults>(response);
      case 500: return customErrorMessage(response, 'Cannot get files from folder. Server error.', true);
      default: {
        if (isNotSuccessCode(response.status)) {
          return customErrorMessage(response, 'An unexpected server error occurred.');
        }
        return of(null);
      }
    }
  }

  /**
   * @return Returns only existing file names.
   */
  public checkExistingFiles = (fileNames: string[]): Observable<string[]> => {
    const url = this.apiEndpoint.CheckExistingFilesUrl;
    const options = {
      body: JSON.stringify(fileNames),
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'api-version': '2.0'
      })
    };

    return this.http.request<HttpResponse<Blob>>('post', url, options as unknown)
      .pipe(mergeMap(this.processCheckExistingFiles))
      .pipe(catchError(tryAddingMoreContextToErrorMessage(this.processCheckExistingFiles)));
  }

  private processCheckExistingFiles = (response: HttpResponse<Blob>): Observable<string[]> => {
    switch(response.status) {
      case 200: return parseSuccessDetails<string[]>(response);
      case 400: return customErrorMessage(response, 'Invalid model', true);
      case 500: return customErrorMessage(response, 'Cannot check existing file names. Server error.', true);
      default: {
        if (isNotSuccessCode(response.status)) {
          return customErrorMessage(response, 'An unexpected server error occurred.');
        }
        return of(null);
      }
    }
  }

  /**
   * @return Return current storage quota as anonymous object.
   */
  public getStorageQuota = (): Observable<any> => {
    const url = this.apiEndpoint.GetStorageQuotaUrl;
    const options = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'api-version': '2.0'
      })
    };
    return this.http.request<HttpResponse<Blob>>('get', url, options as unknown)
      .pipe(mergeMap(this.processGetStorageQuota))
      .pipe(catchError(tryAddingMoreContextToErrorMessage(this.processGetStorageQuota)));
  }

  private processGetStorageQuota = (response: HttpResponse<Blob>): Observable<any> => {
    switch(response.status) {
      case 200: return parseSuccessDetails(response);
      case 500: return customErrorMessage(response, 'Cannot get storage quota. Server error.', true);
      default: {
        if (isNotSuccessCode(response.status)) {
          return customErrorMessage(response, 'An unexpected server error occurred.');
        }
        return of(null);
      }
    }
  }

  /**
   * @return Files were moved
   */
  public moveFiles = (model: MoveFilesModel): Observable<void> => {
    const url = this.apiEndpoint.MoveFilesUrl;
    const options = {
      body: JSON.stringify(model),
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'api-version': '2.0'
      })
    };
    return this.http.request<HttpResponse<Blob>>('put', url, options as unknown)
      .pipe(mergeMap(this.processMoveFiles))
      .pipe(catchError(tryAddingMoreContextToErrorMessage(this.processMoveFiles)));
  }

  private processMoveFiles = (response: HttpResponse<Blob>): Observable<void> => {
    switch(response.status) {
      case 204: return of(null);
      case 400: return customErrorMessage(response, 'Invalid model', true);
      case 500: return customErrorMessage(response, 'Cannot move files. Server error.');
      default: {
        if (isNotSuccessCode(response.status)) {
          return customErrorMessage(response, 'An unexpected server error occurred.');
        }
        return of(null);
      }
    }
  }

  /**
   * @return Entities were deleted
   */
  public deleteEntities = (model: DeleteEntityModel[]): Observable<string[]> => {
    const url = this.apiEndpoint.DeleteEntitiesUrl;
    const options = {
      body: JSON.stringify(model),
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'api-version': '2.0'
      })
    };
    return this.http.request<HttpResponse<Blob>>('post', url, options as unknown)
      .pipe(mergeMap(this.processDeleteEntities))
      .pipe(catchError(tryAddingMoreContextToErrorMessage(this.processDeleteEntities)));
  }

  private processDeleteEntities = (response: HttpResponse<Blob>): Observable<string[]> => {
    switch(response.status) {
      case 200: return parseSuccessDetails<string[]>(response);
      case 400: return customErrorMessage(response, 'Invalid model', true);
      case 500: return customErrorMessage(response, 'Cannot delete entities. Server error.');
      default: {
        if (isNotSuccessCode(response.status)) {
          return customErrorMessage(response, 'An unexpected server error occurred.');
        }
        return of(null);
      }
    }
  }

  public updateFile = (file: FileDto, type: FileModel.FileTypeEnum | null | undefined): Observable<void> => {
    let url = this.apiEndpoint.FilesListUrl;
    url = appendQueryParamToUrlString(url, 'type', type);
    const options = {
      body: JSON.stringify(file),
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'api-version': '2.0'
      })
    };

    return this.http.request<HttpResponse<Blob>>('put', url, options as unknown)
      .pipe(mergeMap(this.processUpdateFile))
      .pipe(catchError(tryAddingMoreContextToErrorMessage(this.processUpdateFile)));
  }

  private processUpdateFile = (response: HttpResponse<Blob>): Observable<void> => {
    switch(response.status) {
      case 200: return of(null);
      case 400: return customErrorMessage(response, 'Invalid model', true);
      case 500: return customErrorMessage(response, 'Error with database connection');
      default: {
        if (isNotSuccessCode(response.status)) {
          return customErrorMessage(response, 'An unexpected server error occurred.');
        }
        return of(null);
      }
    }
  }
}

@Injectable({ providedIn: 'root' })
export class FileUploaderClient {
  constructor(private apiEndpoint: ApiEndpointService, private http: HttpClient) { }

  public upload = (file: FileParameter, parentFolderId: string, type: FileModel.FileTypeEnum, isToCheckQuota?: boolean, keepBoth?: boolean): Observable<FileManagerResult> => {
    const missingParameterMessage = (name) => `The parameter '${name}' must be defined and cannot be null.`
    if ([undefined, null].includes(parentFolderId)) throw new Error(missingParameterMessage('parentFolderId'));
    if ([undefined, null].includes(type)) throw new Error(missingParameterMessage('type'));
    if ([undefined, null].includes(file)) throw new Error(missingParameterMessage('file'));

    let url = this.apiEndpoint.UploadFileUrl;
    url = appendQueryParamToUrlString(url, 'parentFolderId', parentFolderId);
    url = appendQueryParamToUrlString(url, 'type', type);
    url = appendQueryParamToUrlString(url, 'isToCheckQuota', isToCheckQuota);
    url = appendQueryParamToUrlString(url, 'keepBoth', keepBoth);

    const body = new FormData();
    body.append('file', file.data, file.fileName || 'file');
    
    const options = {
      body,
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'api-version': '2.0'
      })
    };
    return this.http.request<HttpResponse<Blob>>('post', url, options as unknown)
      .pipe(mergeMap(this.processUpload))
      .pipe(catchError(tryAddingMoreContextToErrorMessage(this.processUpload)));
  }

  private processUpload = (response: HttpResponse<Blob>): Observable<FileManagerResult> => {
    switch(response.status) {
      case 200: case 201: return parseSuccessDetails<FileManagerResult>(response);
      case 400: return customErrorMessage(response, 'Invalid model', true);
      case 500: return customErrorMessage(response, 'Error with database connection');
      case 507: return customErrorMessage(response, 'Storage limit is exceeded', true);
      default: {
        if (isNotSuccessCode(response.status)) {
          return customErrorMessage(response, 'An unexpected server error occurred.');
        }
        return of(null);
      }
    }
  }
}
